import { Link } from "react-router-dom";

export default function MortgageRefinanceWhenWorthIt() {
    return (
        <>
            <p>
                Refinancing your mortgage can lower your monthly payment, reduce your total interest paid, or let you tap
                home equity — but it's not always worth it. The decision comes down to one number: how long it takes to
                recover the closing costs through monthly savings. If you plan to stay in the home long enough to hit that
                break-even point, refinancing makes sense. If not, you may be better off staying put.
            </p>

            <h2>What Is a Mortgage Refinance?</h2>
            <p>
                A refinance replaces your existing mortgage with a new one. You're essentially paying off your current loan
                and starting a new loan — ideally with better terms. The new loan can have a lower rate, a different term
                length, or a different loan type entirely (e.g., switching from an ARM to a fixed-rate mortgage).
            </p>
            <p>
                There are two main types:
            </p>
            <p>
                <strong>Rate-and-term refinance:</strong> You change the interest rate, the loan term, or both — but you
                don't take cash out. This is the most common refinance goal.
            </p>
            <p>
                <strong>Cash-out refinance:</strong> You borrow more than you owe and receive the difference in cash. Your
                new loan balance is higher, but you get liquidity. Common for home improvements, debt consolidation, or
                large expenses.
            </p>

            <h2>The Break-Even Calculation</h2>
            <p>
                Every refinance has closing costs — typically 2% to 5% of the loan balance. On a $300,000 mortgage, that's
                $6,000 to $15,000 out of pocket (or rolled into the new loan). Your monthly savings from the lower rate have
                to add up to cover those costs before the refinance pays off.
            </p>
            <p>
                Break-even months = Total closing costs ÷ Monthly savings
            </p>
            <p>
                Example: You pay $8,000 in closing costs and save $200/month on your payment. Break-even is 40 months —
                just over 3 years. If you plan to stay in the home at least that long, refinancing makes financial sense.
            </p>
            <p>
                Use the <Link to="/calculator/mortgage">mortgage calculator</Link> to model your current payment vs. a
                refinanced payment at different rates and terms.
            </p>

            <h2>When Refinancing Is Worth It</h2>
            <p>
                <strong>Rates have dropped significantly since you closed.</strong> A common rule of thumb is that
                refinancing makes sense if you can lower your rate by at least 0.75% to 1%. The actual threshold depends
                on your loan balance and how long you'll stay — higher balances and longer timelines make even smaller rate
                drops worth pursuing.
            </p>
            <p>
                <strong>Your credit score has improved substantially.</strong> If your score has jumped 60+ points since
                you got your original loan, you may now qualify for a meaningfully better rate even if market rates haven't
                moved much.
            </p>
            <p>
                <strong>You want to shorten your loan term.</strong> Refinancing from a 30-year to a 15-year mortgage
                typically comes with a lower rate and cuts your total interest paid roughly in half — though your monthly
                payment will be higher.
            </p>
            <p>
                <strong>You're switching from an ARM to a fixed rate.</strong> If you have an adjustable-rate mortgage
                and rates are rising, locking in a fixed rate provides predictability and may save money over the remaining
                loan life.
            </p>
            <p>
                <strong>You want to eliminate PMI.</strong> If your home has appreciated enough that you now have 20%+
                equity, a refinance can remove private mortgage insurance from your payment — sometimes saving $100–$200/month.
            </p>

            <h2>When Refinancing Is Not Worth It</h2>
            <p>
                <strong>You're close to paying off the loan.</strong> In the early years of a mortgage, most of your
                payment is interest. By the time you're 20 years into a 30-year loan, most of your payment is principal.
                Refinancing resets the amortization clock — you'd start paying mostly interest again on a new loan,
                potentially costing more in the long run even at a lower rate.
            </p>
            <p>
                <strong>You're moving soon.</strong> If you're selling in 2 years and the break-even is 4 years, you'll
                absorb the closing costs without recouping them. You'd leave money on the table.
            </p>
            <p>
                <strong>The rate drop is minimal.</strong> Dropping from 7.1% to 6.9% on a $200,000 balance saves about
                $26/month. At $5,000 in closing costs, break-even is 192 months — 16 years. That's almost never worth it
                unless you're planning to stay forever.
            </p>
            <p>
                <strong>You're rolling costs into the loan and extending your term.</strong> If you add $8,000 in closing
                costs to your loan balance and restart at 30 years, you've increased total interest paid significantly.
                Run the full numbers before agreeing to this structure.
            </p>

            <h2>Cash-Out Refinance: Separate Consideration</h2>
            <p>
                A cash-out refinance is a different decision than a rate-and-term refinance. You're not just optimizing
                your mortgage — you're taking on new debt. The key questions are: what is the cash being used for, and
                what's the effective cost compared to alternatives?
            </p>
            <p>
                If you're consolidating credit card debt at 24% APR into a mortgage at 6.5%, the math may work — but
                you're converting unsecured debt to secured debt (your home is now collateral for what used to be a credit
                card bill). If you default, the stakes are different.
            </p>
            <p>
                For home improvements that increase property value, a cash-out refinance at a competitive rate often beats
                a personal loan or HELOC. But compare all three options — the{" "}
                <Link to="/calculator/heloc">HELOC calculator</Link> can help you run the comparison.
            </p>

            <h2>How to Run the Numbers</h2>
            <p>
                Before calling a lender, run a quick estimate yourself:
            </p>
            <p>
                1. Get your current loan balance, remaining term, and interest rate.<br />
                2. Estimate your new rate based on current market rates and your credit profile.<br />
                3. Calculate the monthly savings (current payment minus projected new payment).<br />
                4. Estimate closing costs (2%–5% of loan balance as a starting point).<br />
                5. Divide closing costs by monthly savings to get break-even months.<br />
                6. Compare break-even to how long you plan to stay in the home.
            </p>
            <p>
                If break-even is less than your planned stay, refinancing likely makes sense. If it's longer, it probably
                doesn't. Use the <Link to="/calculator/mortgage">mortgage calculator</Link> to model payment scenarios
                side by side before committing.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                Refinancing is a tool, not a default move. The break-even analysis tells you whether the upfront cost is
                worth the long-term savings given your timeline. Factor in how many years remain on your current loan,
                whether you're resetting the amortization clock, and what you'd do with the monthly savings. When the math
                lines up and your plans align with the break-even window, refinancing can be one of the most impactful
                financial decisions a homeowner makes.
            </p>
        </>
    );
}